import { pulse, SPRING_UI } from '../motion/motion';

/**
 * Each control answers in its own way — the gesture says what the action
 * does, once, and the icon is still again. Nothing loops; a repeated press
 * restarts the gesture rather than stacking it.
 *
 * Gestures use the individual `scale`, `rotate` and `translate` properties
 * (or stroke drawing), so a control's lasting state — a heavier bold, a
 * slanted italic — is free to transition underneath them.
 */

export type IconGesture = 'bold' | 'italic' | 'strike' | 'code' | 'bullet' | 'ordered' | 'quote' | 'link' | 'gear' | 'save';

const SPRING = SPRING_UI.css;

export function iconGesture(host: Element | null, kind: IconGesture, on = true): void {
  const svg = host?.querySelector('svg');
  if (!svg) return;
  const q = (sel: string) => svg.querySelectorAll(sel);
  switch (kind) {
    case 'bold':
      // It gains weight: a firm press outward, then rest.
      pulse(svg, [{ scale: '1' }, { scale: on ? '1.16' : '0.88', offset: 0.35 }, { scale: '1' }], { duration: 340, easing: 'cubic-bezier(0.3, 0, 0.2, 1)' });
      break;
    case 'italic':
      pulse(svg, [{ rotate: '0deg' }, { rotate: on ? '-12deg' : '8deg', offset: 0.4 }, { rotate: '0deg' }], { duration: 380, easing: 'cubic-bezier(0.3, 0, 0.2, 1)' });
      break;
    case 'strike':
      // The line is drawn through (or lifted off) the letter.
      pulse(q('.i-strike-line')[0], on ? [{ strokeDasharray: '1', strokeDashoffset: '1' }, { strokeDasharray: '1', strokeDashoffset: '0' }] : [{ strokeDasharray: '1', strokeDashoffset: '0' }, { strokeDasharray: '1', strokeDashoffset: '-1', offset: 0.5 }, { strokeDasharray: '1', strokeDashoffset: '-1', opacity: 0, offset: 0.55 }, { strokeDasharray: '1', strokeDashoffset: '0', opacity: 1 }], { duration: on ? 320 : 420, easing: 'cubic-bezier(0.5, 0, 0.2, 1)' });
      break;
    case 'code':
      pulse(q('.i-code-l')[0], [{ translate: '0px' }, { translate: on ? '-1.6px' : '1px', offset: 0.4 }, { translate: '0px' }], { duration: 360, easing: SPRING });
      pulse(q('.i-code-r')[0], [{ translate: '0px' }, { translate: on ? '1.6px' : '-1px', offset: 0.4 }, { translate: '0px' }], { duration: 360, easing: SPRING });
      break;
    case 'bullet':
      // The items arrive one after another.
      q('.i-dot').forEach((d, i) => pulse(d, [{ scale: '0', transformOrigin: 'center', transformBox: 'fill-box' }, { scale: '1', transformOrigin: 'center', transformBox: 'fill-box' }], { duration: 260, delay: i * 60, easing: SPRING }));
      break;
    case 'ordered':
      q('.i-line').forEach((l, i) => pulse(l, [{ strokeDasharray: '1', strokeDashoffset: '1' }, { strokeDasharray: '1', strokeDashoffset: '0' }], { duration: 240, delay: i * 70, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }));
      break;
    case 'quote':
      pulse(q('.i-quote-bar')[0], [{ scale: '1 0', transformOrigin: 'top', transformBox: 'fill-box' }, { scale: '1 1', transformOrigin: 'top', transformBox: 'fill-box' }], { duration: 320, easing: 'cubic-bezier(0.3, 0, 0.1, 1)' });
      break;
    case 'link':
      // The two links close on each other and lock.
      pulse(q('.i-link-a')[0], [{ translate: '0px 0px' }, { translate: on ? '-1.3px 1.3px' : '1px -1px', offset: 0.45 }, { translate: '0px 0px' }], { duration: 380, easing: SPRING });
      pulse(q('.i-link-b')[0], [{ translate: '0px 0px' }, { translate: on ? '1.3px -1.3px' : '-1px 1px', offset: 0.45 }, { translate: '0px 0px' }], { duration: 380, easing: SPRING });
      break;
    case 'gear':
      // A deliberate sixth of a turn: six teeth, so it rests looking exactly as before.
      pulse(q('.i-gear')[0], [{ rotate: '0deg', transformOrigin: '10px 10px' }, { rotate: on ? '60deg' : '-60deg', transformOrigin: '10px 10px' }], { duration: 560, easing: 'cubic-bezier(0.45, 0, 0.15, 1)' });
      break;
    case 'save':
      pulse(q('.i-check')[0], [{ strokeDasharray: '1', strokeDashoffset: '1' }, { strokeDasharray: '1', strokeDashoffset: '0' }], { duration: 420, delay: 60, easing: 'cubic-bezier(0.5, 0, 0.15, 1)' });
      break;
  }
}
